import { DatePicker as AntdDatePicker, type DatePickerProps } from 'antd';

import { dateFormat } from '@core/format/dates';

/**
 * The template's date picker, which is Ant Design's date picker.
 *
 * Worth stating plainly, because it is the answer to "why not use the theme's component": the
 * theme's `CommonDatePicker` *is* this. The template has no calendar of its own — it wraps antd's
 * in a `.common-datePicker` div and styles it from `theme-antd.css`. So adopting the template's
 * component means adopting that wrapper, not replacing the control.
 *
 * The wrapper is not cosmetic. Without it the picker keeps antd's own 32px height and border
 * colour and sits a visible pixel out of line with every `Input` beside it, and the calendar panel
 * keeps antd's default font. Both were the case before this component existed.
 *
 * `format` follows the app rather than the template, and until now it only said so. The prop was
 * never passed, which left the calendar on antd's `en_GB` default — right by coincidence while the
 * app's own format was hardcoded to the same thing, and wrong the moment a company could choose
 * something else. It now resolves `dateFormat()` on every render, so a picker can never write
 * 1/4/2026 beside a table reading 01/04/2026. A caller may still pass its own `format` and win.
 */
export function DatePicker({ className = '', ...props }: DatePickerProps) {
  return (
    <div className={`common-datePicker ${className}`}>
      <AntdDatePicker
        format={dateFormat()}
        // The calendar glyph is Lucide, matching every other icon in the app. antd's own is an
        // inline SVG from `@ant-design/icons`, which is the one place that package would still be
        // pulled in — and it does not match the icons on either side of it.
        suffixIcon={<i className="icon icon-calendar-fold text-dark" aria-hidden="true" />}
        {...props}
      />
    </div>
  );
}
